import { StatusCodes } from "http-status-codes";
import { Component, OnInit } from "@angular/core";
import * as XLSX from "xlsx";
import { Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { Message } from "../../../models/message";
import { addMessage } from "../../../store/actions/message.actions";
import { UPLOAD_STATUS, UploadStatus } from "../../../models/uploadStatus.model";
import { BPOSTSyncService, UploadBPOSTLabel } from "../../../services/bpost-sync.service";
import { MAX_LENGTH } from "../../../helpers/constants/fieldSize";
import { finishBusy, startBusy } from "src/app/store/actions/busy.actions";
import { parseFullName } from "src/app/helpers/parseFullname";

interface BPOSTRawCSVRow {
    barcode?: string | null;
    leveringsmethode?: string | null;
    "adres bestemmeling"?: string | null;
    "contact contactpersoon"?: string | null;
    naam?: string | null;
    straat?: string | null;
    "n°"?: string | null;
    bus?: string | null;
    postcode?: string | null;
    gemeente?: string | null;
    land?: string | null;
    status?: string | null;
    datum?: string | null;
    "ref. bestelling"?: string | null;
    kostenplaats?: string | null;
    "contact telefoonnummer"?: string | null;
    "vrije boodschap"?: string | null;
    [key: string]: any;
}

export enum CSV_UPLOAD_STATUS {
    NO_FILE_SELECTED,
    CSV_UPLOADING,
    CSV_UPLOADED,
    CSV_PARSED,
    CSV_IN_PROGRESS,
    ADDING_LABELS,
    LABELS_ADDED,
    CSV_UPLOAD_FAILED,
}

@Component({
    selector: "app-sync-bpost-labels",
    templateUrl: "./sync-bpost-labels.component.html",
    styleUrls: ["./sync-bpost-labels.component.scss"],
})
export class SyncBPOSTLabelsComponent implements OnInit {
    public currentLocalStatus: CSV_UPLOAD_STATUS = CSV_UPLOAD_STATUS.NO_FILE_SELECTED;
    public fileName: string = "";
    public parsedRows: BPOSTRawCSVRow[] = [];
    public validationErrors: { fields: string[]; reason: string }[] | null = null;

    public latestUploadStatus: UploadStatus | null = null;
    public isLoadingDetails: boolean = false;

    public UPLOAD_STATUSSES = UPLOAD_STATUS;
    public csvUploadStatusEnum = CSV_UPLOAD_STATUS;

    private readonly requiredHeaders = ["barcode", "postcode", "naam", "straat", "n°"];

    constructor(private backend: BPOSTSyncService, private router: Router, private store: Store) {}

    ngOnInit() {
        this.fetchLatestUploadStatus();
    }

    fetchLatestUploadStatus() {
        this.isLoadingDetails = true;
        this.backend.getLatestUploadStatus().subscribe({
            next: (status) => {
                this.latestUploadStatus = status;

                if (this.latestUploadStatus && !this.latestUploadStatus.isCompleted) {
                    this.currentLocalStatus = CSV_UPLOAD_STATUS.CSV_IN_PROGRESS;
                } else {
                    if (this.currentLocalStatus === CSV_UPLOAD_STATUS.CSV_IN_PROGRESS) {
                        this.currentLocalStatus = CSV_UPLOAD_STATUS.NO_FILE_SELECTED;
                    }
                }
                this.isLoadingDetails = false;
            },
            error: (err) => {
                console.error("Failed to fetch latest upload status", err);

                this.latestUploadStatus = null;
                this.currentLocalStatus = CSV_UPLOAD_STATUS.NO_FILE_SELECTED;
                this.isLoadingDetails = false;

                this.store.dispatch(
                    addMessage({
                        message: Message.createErrorMessage(
                            "Fout",
                            "Kon laatste upload status niet ophalen.",
                        ),
                    }),
                );
            },
        });
    }

    refreshSelectedStatus() {
        if (!this.latestUploadStatus) {
            this.fetchLatestUploadStatus();
            return;
        }

        const selectedId = this.latestUploadStatus.id;
        this.isLoadingDetails = true;
        this.backend.getUploadStatusById(selectedId).subscribe({
            next: (refreshedStatus) => {
                if (refreshedStatus) {
                    this.latestUploadStatus = refreshedStatus;

                    if (this.latestUploadStatus && !this.latestUploadStatus.isCompleted) {
                        this.currentLocalStatus = CSV_UPLOAD_STATUS.CSV_IN_PROGRESS;
                    } else {
                        if (this.currentLocalStatus === CSV_UPLOAD_STATUS.CSV_IN_PROGRESS) {
                            this.currentLocalStatus = CSV_UPLOAD_STATUS.NO_FILE_SELECTED;
                        }
                        this.fileName = "";
                        this.parsedRows = [];
                        this.validationErrors = null;
                    }
                } else {
                    this.store.dispatch(
                        addMessage({
                            message: Message.createNotification(
                                "Status Niet Gevonden",
                                `Upload #${selectedId} niet meer gevonden. Lijst wordt vernieuwd.`,
                            ),
                        }),
                    );
                    this.fetchLatestUploadStatus();
                }
                this.isLoadingDetails = false;
            },
            error: (err) => {
                console.error(`Failed to refresh status ID ${selectedId}`, err);
                this.store.dispatch(
                    addMessage({
                        message: Message.createErrorMessage(
                            "Fout",
                            "Kon geselecteerde status niet verversen.",
                        ),
                    }),
                );
                this.isLoadingDetails = false;
            },
        });
    }

    getRemainingAmount(status: UploadStatus | null): number {
        if (!status) return 0;
        return Math.max(
            0,
            status.totalAmount - status.successAmount - status.erroredAmount - status.skippedAmount,
        );
    }

    changeListener = (event: Event): void => {
        const target = event.target as HTMLInputElement;
        const file = target?.files?.[0];

        if (file) {
            if (this.latestUploadStatus && !this.latestUploadStatus.isCompleted) {
                this.store.dispatch(
                    addMessage({
                        message: Message.createNotification(
                            "Upload Actief",
                            "Kan geen nieuw bestand selecteren terwijl de laatste upload nog bezig is.",
                        ),
                    }),
                );
                if (target) target.value = "";
                return;
            }
            this.currentLocalStatus = CSV_UPLOAD_STATUS.CSV_UPLOADING;
            this.fileName = file.name;
            this.parsedRows = [];
            this.validationErrors = null;
            this.readAndParseFile(file);
        } else {
            this.currentLocalStatus = CSV_UPLOAD_STATUS.NO_FILE_SELECTED;
            this.fileName = "";
            this.parsedRows = [];
            this.validationErrors = null;
        }

        if (target) target.value = "";
    };

    readAndParseFile = (file: File): void => {
        const reader: FileReader = new FileReader();

        reader.onload = (e: ProgressEvent<FileReader>) => {
            this.store.dispatch(startBusy());
            try {
                const binaryString = e.target?.result;
                if (typeof binaryString !== "string") {
                    throw new Error("FileReader result is not a string.");
                }

                const workbook = XLSX.read(binaryString, { type: "binary" });
                const firstSheetName = workbook.SheetNames[0];
                if (!firstSheetName) throw new Error("CSV bevat geen werkbladen.");

                const worksheet = workbook.Sheets[firstSheetName];
                const sheetData = XLSX.utils.sheet_to_json<any[]>(worksheet, {
                    header: 1,
                    blankrows: false,
                });

                if (sheetData.length < 2) {
                    throw new Error(
                        "Bestand bevat geen geldige gegevensrijen (minimaal koptekst + 1 rij nodig).",
                    );
                }

                const headers = (sheetData[0] as any[]).map((headerValue, index) => {
                    if (typeof headerValue === "string" && headerValue.trim().length > 0) {
                        let cleanedHeader = headerValue.trim().toLowerCase();
                        cleanedHeader = cleanedHeader.replace(/\./g, "");

                        return cleanedHeader;
                    } else {
                        return `empty_header_${index}`;
                    }
                });
                const dataRows = sheetData.slice(1);

                const missingRequiredHeaders = this.requiredHeaders.filter(
                    (h) => !headers.includes(h),
                );
                if (missingRequiredHeaders.length > 0) {
                    console.warn(
                        "CSV missing required headers:",
                        missingRequiredHeaders,
                        "Found:",
                        headers,
                    );
                    this.store.dispatch(
                        addMessage({
                            message: Message.createNotification(
                                "Waarschuwing: Kolommen Ontbreken",
                                `Vereiste kolomkoppen missen: ${missingRequiredHeaders.join(
                                    ", ",
                                )}. Rijen zonder deze data worden overgeslagen.`,
                            ),
                        }),
                    );
                }

                this.parsedRows = dataRows
                    .map((rowArray: any[]) => {
                        const rowData: any = {};
                        headers.forEach((header, index) => {
                            const value = rowArray[index];

                            rowData[header] = typeof value === "string" ? value.trim() : value;
                        });
                        return rowData as BPOSTRawCSVRow;
                    })

                    .filter((row) => row.barcode);

                if (this.parsedRows.length === 0) {
                    throw new Error(
                        "Geen geldige rijen met een barcode gevonden in het bestand na parsing en filtering.",
                    );
                }

                this.currentLocalStatus = CSV_UPLOAD_STATUS.CSV_PARSED;
                this.store.dispatch(finishBusy());
                this.store.dispatch(
                    addMessage({
                        message: Message.createSuccessMessage(
                            "Bestand Gelezen",
                            `${this.parsedRows.length} rijen klaar voor synchronisatie uit ${this.fileName}.`,
                        ),
                    }),
                );
            } catch (error: any) {
                console.error("Error reading/parsing file:", error);
                this.store.dispatch(finishBusy());
                this.store.dispatch(
                    addMessage({
                        message: Message.createErrorMessage(
                            "Fout bij Verwerken Bestand",
                            error.message || "Onbekende fout",
                        ),
                    }),
                );
                this.currentLocalStatus = CSV_UPLOAD_STATUS.CSV_UPLOAD_FAILED;
                this.fileName = "";
                this.parsedRows = [];
            }
        };

        reader.onerror = (error) => {
            console.error("FileReader error:", error);
            this.store.dispatch(finishBusy());
            this.store.dispatch(
                addMessage({
                    message: Message.createErrorMessage("Leesfout", "Kon het bestand niet lezen."),
                }),
            );
            this.currentLocalStatus = CSV_UPLOAD_STATUS.CSV_UPLOAD_FAILED;
        };

        reader.readAsBinaryString(file);
    };

    get csvIsParsed(): boolean {
        return this.currentLocalStatus === this.csvUploadStatusEnum.CSV_PARSED;
    }

    buildAndSyncData = () => {
        if (!this.csvIsParsed || !this.parsedRows || this.parsedRows.length === 0) {
            this.store.dispatch(
                addMessage({
                    message: Message.createNotification(
                        "Geen Data",
                        "Selecteer en parseer eerst een geldig CSV-bestand.",
                    ),
                }),
            );
            return;
        }

        if (this.latestUploadStatus && !this.latestUploadStatus.isCompleted) {
            this.store.dispatch(
                addMessage({
                    message: Message.createNotification(
                        "Upload Actief",
                        "Wacht tot de laatste upload is voltooid voordat u een nieuwe start.",
                    ),
                }),
            );
            return;
        }

        this.currentLocalStatus = CSV_UPLOAD_STATUS.ADDING_LABELS;
        this.validationErrors = null;
        this.store.dispatch(startBusy());

        const originalRowCount = this.parsedRows.length;
        let uploadPayload: UploadBPOSTLabel[] = [];

        try {
            uploadPayload = this.parsedRows
                .filter((row: BPOSTRawCSVRow) => {
                    const nameToParse = row.naam || row["contact contactpersoon"];

                    return !(
                        !nameToParse ||
                        !row.barcode ||
                        !row["postcode"] ||
                        !row.straat ||
                        !row["n°"]
                    );
                })
                .map((row: BPOSTRawCSVRow): UploadBPOSTLabel => {
                    const nameToParse = row.naam || row["contact contactpersoon"] || "";
                    const parsedName = parseFullName(nameToParse);

                    return {
                        reference: row.barcode,
                        recipient: {
                            initials: "",
                            firstname:
                                parsedName.firstName?.substring(0, MAX_LENGTH.LASTNAME) || "",
                            middlename: parsedName.middleName,
                            lastname: parsedName.lastName || "",
                            email: "no-reply@domain.com",
                            streetname: row.straat || "",
                            housenr: row["n°"] || "",
                            housenr_extra: row.bus,
                            address_extra: "",
                            postcode: row["postcode"] || "",
                            city: row.gemeente || "",
                            country: row.land || "",
                            phone:
                                row["contact telefoonnummer"]?.substring(0, MAX_LENGTH.PHONE) || "",
                            company_name: "",
                        },
                        deliveryMethod: row.leveringsmethode,
                        orderReference: row["ref. bestelling"],
                        costCenter: row.kostenplaats,
                        message: row["vrije boodschap"],
                        status: row.status,
                        date: row.datum,
                    };
                });
        } catch (mapError: any) {
            console.error("Error mapping CSV data:", mapError);
            this.store.dispatch(finishBusy());
            this.store.dispatch(
                addMessage({
                    message: Message.createErrorMessage(
                        "Verwerkingsfout",
                        `Kon CSV gegevens niet correct omzetten: ${mapError.message}`,
                    ),
                }),
            );
            this.currentLocalStatus = CSV_UPLOAD_STATUS.CSV_UPLOAD_FAILED;
            return;
        }

        const validRowCount = uploadPayload.length;
        const skippedRowCount = originalRowCount - validRowCount;

        if (skippedRowCount > 0) {
            const summary = `${skippedRowCount} rijen overgeslagen wegens ontbrekende essentiële gegevens.`;

            this.store.dispatch(
                addMessage({ message: Message.createNotification("Rijen Overgeslagen", summary) }),
            );
        }

        if (validRowCount > 0) {
            this.syncLabelsWithBackend(uploadPayload);
        } else {
            this.store.dispatch(finishBusy());
            this.store.dispatch(
                addMessage({
                    message: Message.createErrorMessage(
                        "Geen Geldige Data",
                        "Geen rijen gevonden om te uploaden na validatie.",
                    ),
                }),
            );
            this.currentLocalStatus = CSV_UPLOAD_STATUS.CSV_UPLOAD_FAILED;
        }
    };

    syncLabelsWithBackend = (labels: UploadBPOSTLabel[]) => {
        this.store.dispatch(startBusy());
        this.validationErrors = null;

        this.backend.syncBPOSTLabels(labels).subscribe({
            next: (backendStatusResponse: UploadStatus | null) => {
                this.store.dispatch(finishBusy());

                this.fetchLatestUploadStatus();

                if (backendStatusResponse) {
                    const status = backendStatusResponse.status;
                    if (status === UPLOAD_STATUS.WORKING || status === UPLOAD_STATUS.STALLED) {
                        this.store.dispatch(
                            addMessage({
                                message: Message.createSuccessMessage(
                                    "Upload Gestart",
                                    `Upload is gestart/bezig (${status}). ${labels.length} records worden verwerkt. De status wordt bijgewerkt.`,
                                ),
                            }),
                        );
                        this.fileName = "";
                        this.parsedRows = [];
                        this.currentLocalStatus = CSV_UPLOAD_STATUS.CSV_IN_PROGRESS;
                    } else if (status === UPLOAD_STATUS.DONE) {
                        this.store.dispatch(
                            addMessage({
                                message: Message.createSuccessMessage(
                                    "Upload Voltooid",
                                    `Synchronisatie voltooid (Resultaat: ${backendStatusResponse.successAmount} succes, ${backendStatusResponse.erroredAmount} fout, ${backendStatusResponse.skippedAmount} overgeslagen).`,
                                ),
                            }),
                        );
                        this.fileName = "";
                        this.parsedRows = [];
                        this.currentLocalStatus = CSV_UPLOAD_STATUS.LABELS_ADDED;
                    } else {
                        this.store.dispatch(
                            addMessage({
                                message: Message.createErrorMessage(
                                    "Upload Mislukt",
                                    `Upload mislukt/gestopt met status: ${status}.`,
                                ),
                            }),
                        );
                        this.currentLocalStatus = CSV_UPLOAD_STATUS.CSV_UPLOAD_FAILED;
                    }
                } else {
                    this.store.dispatch(
                        addMessage({
                            message: Message.createErrorMessage(
                                "Fout",
                                "Geen bevestiging ontvangen na start upload. Ververs de status om te controleren.",
                            ),
                        }),
                    );
                    this.currentLocalStatus = CSV_UPLOAD_STATUS.CSV_UPLOAD_FAILED;
                }
            },
            error: (errorResponse: any) => {
                this.store.dispatch(finishBusy());
                console.error("BPOST Sync Backend Error:", errorResponse);
                this.currentLocalStatus = CSV_UPLOAD_STATUS.CSV_UPLOAD_FAILED;

                this.latestUploadStatus = null;

                if (
                    errorResponse.status === StatusCodes.UNPROCESSABLE_ENTITY &&
                    errorResponse.error?.errors
                ) {
                    this.validationErrors = errorResponse.error.errors;
                    this.store.dispatch(
                        addMessage({
                            message: Message.createErrorMessage(
                                "Validatiefouten",
                                "Server retourneerde validatiefouten.",
                            ),
                        }),
                    );
                } else {
                    const message =
                        errorResponse.error?.message ||
                        errorResponse.message ||
                        "Onbekende serverfout.";
                    this.store.dispatch(
                        addMessage({
                            message: Message.createErrorMessage(
                                "Synchronisatiefout",
                                `Kon niet synchroniseren: ${message}`,
                            ),
                        }),
                    );
                }
                this.fetchLatestUploadStatus();
            },
        });
    };

    close = () => {
        this.router.navigateByUrl("/dashboard");
    };

    handleStopSync(): void {
        if (!this.latestUploadStatus || this.latestUploadStatus.isCompleted) {
            this.store.dispatch(
                addMessage({
                    message: Message.createNotification(
                        "Geen actieve upload",
                        "Er is geen actieve upload om te stoppen.",
                    ),
                }),
            );
            return;
        }

        const uploadId = this.latestUploadStatus.id;
        this.store.dispatch(startBusy());
        this.backend.stopSync(uploadId).subscribe({
            next: () => {
                this.store.dispatch(finishBusy());
                this.store.dispatch(
                    addMessage({
                        message: Message.createSuccessMessage(
                            "Stopverzoek verzonden",
                            "Het verzoek om de upload te stoppen is verzonden.",
                        ),
                    }),
                );
                this.fetchLatestUploadStatus();
            },
            error: (err) => {
                this.store.dispatch(finishBusy());
                console.error(`Failed to stop upload ${uploadId}`, err);
                this.store.dispatch(
                    addMessage({
                        message: Message.createErrorMessage("Fout", "Kon de upload niet stoppen."),
                    }),
                );
            },
        });
    }
}
